import { Icon } from '@civilgrid/ui/components';
import { Indicator, Root } from '@radix-ui/react-checkbox';
import classNames from 'classnames';
import { isBoolean } from 'lodash';
import uniqueId from 'lodash/uniqueId';
import { useEffect, useState } from 'react';

interface CheckboxProps {
  isDisabled?: boolean;
  isInitiallyChecked?: boolean;
  text?: string;
  color?: 'primary' | 'brand';
  checked?: boolean;
  labelClassName?: string;
  onChange: (value: boolean | 'indeterminate') => void;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * A control that allows the user to toggle between checked and not checked.
 *
 * @deprecated Use `Checkbox` from `@civilgrid/ui/components` instead. Kept only for
 * civil-search, which is being retired in favour of GridTools. Do not add new usages.
 */
export const Checkbox = ({
  isDisabled = false,
  isInitiallyChecked = false,
  text,
  color = 'primary',
  checked,
  onChange,
  labelClassName,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: CheckboxProps) => {
  const [isChecked, setIsChecked] = useState<boolean | 'indeterminate'>(isInitiallyChecked);
  const cgCheckboxId = uniqueId();

  useEffect(() => {
    if (isBoolean(checked)) {
      setIsChecked(checked);
    }
  }, [checked]);

  const onCheckedChange = (data: boolean | 'indeterminate') => {
    setIsChecked(data);
    onChange(data);
  };

  return (
    <form>
      <div className="flex items-center gap-2">
        <Root
          data-slot="checkbox"
          className={classNames(
            'peer size-4 shrink-0 rounded-[4px] border border-grey-30 shadow-xs transition-shadow',
            'cursor-pointer',
            'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary',
            'focus-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'aria-invalid:border-red-50',
          )}
          defaultChecked={isInitiallyChecked}
          id={cgCheckboxId}
          disabled={isDisabled}
          aria-label={ariaLabel}
          data-testid={testId}
          onCheckedChange={(data) => onCheckedChange(data)}
          checked={isChecked}
        >
          <Indicator
            forceMount
            data-slot="checkbox-indicator"
            className="grid place-content-center text-current transition-none data-[state=unchecked]:hidden data-[state=indeterminate]:hidden"
          >
            <Icon icon="luCheck" size={16} className="text-white" />
          </Indicator>
          <Indicator
            forceMount
            data-slot="checkbox-indicator"
            className="grid place-content-center text-current transition-none data-[state=checked]:hidden data-[state=unchecked]:hidden"
          >
            <Icon icon="luMinus" size={16} className="text-white" />
          </Indicator>
        </Root>
        {text && (
          <label
            className={classNames('grow cursor-pointer text-primary text-xs', labelClassName)}
            htmlFor={cgCheckboxId}
          >
            <span>{text}</span>
          </label>
        )}
      </div>
    </form>
  );
};
